/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { classes, VComponent } from '@adobe-commerce/elsie/lib';
import { Skeleton, SkeletonRow } from '@adobe-commerce/elsie/components';

import '@/quote-management/components/ManageNegotiableQuoteTemplate/ManageNegotiableQuoteTemplate.css';

export interface ManageNegotiableQuoteTemplateProps extends Omit<HTMLAttributes<HTMLDivElement>, 'loading'> {
  loading?: boolean;
  templateName: VNode;
  templateStatus: VNode;
  banner?: VNode;
  details?: VNode;
  actionBar?: VNode;
  referenceDocuments?: VNode;
  itemsTable: VNode;
  shippingInformationTitle?: VNode;
  shippingInformation?: VNode;
  commentsTitle: VNode;
  comments: VNode;
  attachFilesField?: VNode;
  attachedFilesList?: VNode;
  footer?: VNode;
}

export const ManageNegotiableQuoteTemplate: FunctionComponent<ManageNegotiableQuoteTemplateProps> = ({
  className,
  loading,
  templateName,
  templateStatus,
  banner,
  details,
  actionBar,
  referenceDocuments,
  itemsTable,
  shippingInformationTitle,
  shippingInformation,
  commentsTitle,
  comments,
  attachFilesField,
  attachedFilesList,
  footer,
  ...props
}) => {
  if (loading) {
    return <ManageNegotiableQuoteTemplateSkeleton />;
  }

  return (
    <div {...props} className={classes(['quote-management-manage-negotiable-quote-template', className])}>
      <div className={classes(['quote-management-manage-negotiable-quote-template__header'])}>
        {templateName &&
          <VComponent
            node={templateName}
            className={classes(['quote-management-manage-negotiable-quote-template__template-name'])}
          />
        }
        {templateStatus &&
          <VComponent
            node={templateStatus}
            className={classes(['quote-management-manage-negotiable-quote-template__template-status'])}
          />
        }
      </div>
      {banner &&
        <VComponent
          node={banner}
          className={classes(['quote-management-manage-negotiable-quote-template__banner'])}
        />
      }
      {details &&
        <VComponent
          node={details}
          className={classes(['quote-management-manage-negotiable-quote-template__details'])}
        />
      }
      {actionBar &&
        <VComponent
          node={actionBar}
          className={classes(['quote-management-manage-negotiable-quote-template__action-bar'])}
        />
      }
      {referenceDocuments &&
        <div data-testid="reference-documents-container" className={classes(['quote-management-manage-negotiable-quote-template__reference-documents-container'])}>
          <VComponent
            node={referenceDocuments}
            className={classes(['quote-management-manage-negotiable-quote-template__reference-documents'])}
          />
        </div>
      }
      {itemsTable &&
        <VComponent
          node={itemsTable}
          className={classes(['quote-management-manage-negotiable-quote-template__items-table'])}
        />
      }
      <div data-testid="template-shipping-information-container" className={classes(['quote-management-manage-negotiable-quote-template__shipping-information-container'])}>
        {shippingInformationTitle &&
          <VComponent
            node={shippingInformationTitle}
            className={classes(['quote-management-manage-negotiable-quote-template__shipping-information-title'])}
          />
        }
        {shippingInformation &&
          <VComponent
            node={shippingInformation}
            className={classes(['quote-management-manage-negotiable-quote-template__shipping-information'])}
          />
        }
      </div>
      <div data-testid="comments-container" className={classes(['quote-management-manage-negotiable-quote-template__comments-container'])}>
        {commentsTitle &&
          <VComponent
            node={commentsTitle}
            className={classes(['quote-management-manage-negotiable-quote-template__comments-title'])}
          />
        }
        {comments &&
          <VComponent
            node={comments}
            className={classes(['quote-management-manage-negotiable-quote-template__comments'])}
          />
        }
        {attachFilesField &&
          <VComponent
            node={attachFilesField}
            className={classes(['quote-management-manage-negotiable-quote-template__attach-files-field'])}
          />
        }
        {attachedFilesList &&
          <VComponent
            node={attachedFilesList}
            className={classes(['quote-management-manage-negotiable-quote-template__attached-files-list'])}
          />
        }
      </div>
      {footer &&
        <VComponent
          node={footer}
          className={classes(['quote-management-manage-negotiable-quote-template__footer'])}
        />
      }
    </div>
  );
};

export const ManageNegotiableQuoteTemplateSkeleton: FunctionComponent = () => {
  return (
    <Skeleton data-testid="manage-negotiable-quote-template-skeleton">
      <SkeletonRow variant="heading" fullWidth={true} size="large" />
      <SkeletonRow variant="row" fullWidth={false} size="small" />
      <SkeletonRow variant="row" fullWidth={false} size="small" />
      <SkeletonRow variant="row" fullWidth={true} />
      <SkeletonRow variant="row" fullWidth={true} size="xlarge" lines={2} />
      <SkeletonRow variant="row" fullWidth={false} size="small" />
    </Skeleton>
  );
};
